(() => {
  const config = window.atlistTheme || {};
  const routes = config.routes || {};
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const liveRegion = () => $('[data-live-region]');

  const announce = (message) => {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const formatMoney = (cents = 0) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: config.currency || 'USD'
      }).format(Number(cents) / 100);
    } catch (_) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  const setBodyLock = () => {
    const isOpen = $('[data-mobile-menu].open') || $('[data-search-overlay].open') || $('[data-cart-drawer].open') || $('[data-filter-panel].open');
    document.body.classList.toggle('noScroll', Boolean(isOpen));
  };

  const mobileMenu = $('[data-mobile-menu]');
  const openMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.add('open');
    mobileMenu.setAttribute('aria-hidden', 'false');
    setBodyLock();
    $('[data-menu-close]', mobileMenu)?.focus();
  };
  const closeMenu = () => {
    if (!mobileMenu) return;
    mobileMenu.classList.remove('open');
    mobileMenu.setAttribute('aria-hidden', 'true');
    setBodyLock();
  };

  const searchOverlay = $('[data-search-overlay]');
  const searchInput = $('[data-predictive-input]');
  const searchResults = $('[data-predictive-results]');
  let searchTimer;

  const openSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.classList.add('open');
    searchOverlay.setAttribute('aria-hidden', 'false');
    setBodyLock();
    window.setTimeout(() => searchInput?.focus(), 180);
  };
  const closeSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.classList.remove('open');
    searchOverlay.setAttribute('aria-hidden', 'true');
    setBodyLock();
  };

  const renderPredictiveSearch = (products, query) => {
    if (!searchResults) return;
    if (!products.length) {
      searchResults.innerHTML = `<p class="predictiveEmpty">No products found for “${escapeHtml(query)}”.</p>`;
      return;
    }
    searchResults.innerHTML = products.map((product) => `
      <a href="${escapeHtml(product.url)}" class="predictiveResult">
        ${product.image ? `<img src="${escapeHtml(product.image)}&width=160" alt="" width="72" height="80">` : '<span class="predictiveImageBlank"></span>'}
        <span><strong>${escapeHtml(product.title)}</strong><small>${formatMoney(product.price)}</small></span>
        <span aria-hidden="true">→</span>
      </a>
    `).join('');
  };

  const requestPredictiveSearch = async (query) => {
    if (!query.trim()) {
      if (searchResults) searchResults.innerHTML = '';
      return;
    }
    try {
      const root = routes.root || '/';
      const url = `${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search request failed');
      const data = await response.json();
      renderPredictiveSearch(data.resources?.results?.products || [], query);
    } catch (_) {
      if (searchResults) searchResults.innerHTML = '<p class="predictiveEmpty">Search is temporarily unavailable. Press Enter to continue.</p>';
    }
  };

  searchInput?.addEventListener('input', (event) => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => requestPredictiveSearch(event.target.value), 220);
  });

  const cartDrawer = $('[data-cart-drawer]');
  const cartBackdrop = $('[data-cart-backdrop]');
  const cartContent = $('[data-cart-drawer-content]');

  const openCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('open');
    cartBackdrop?.classList.add('open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    setBodyLock();
    $('[data-cart-close]', cartDrawer)?.focus();
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('open');
    cartBackdrop?.classList.remove('open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    setBodyLock();
  };

  const cartItemHtml = (item) => `
    <article data-cart-line data-key="${escapeHtml(item.key)}">
      <a class="cartItemImage" href="${escapeHtml(item.url)}">
        ${item.image ? `<img src="${escapeHtml(item.image)}&width=240" alt="${escapeHtml(item.product_title)}" width="105" height="122">` : ''}
      </a>
      <div>
        <a href="${escapeHtml(item.url)}"><h3>${escapeHtml(item.product_title)}${item.variant_title && item.variant_title !== 'Default Title' ? `<span>${escapeHtml(item.variant_title)}</span>` : ''}</h3></a>
        <div class="cartItemBottom">
          <div class="miniQuantity">
            <button type="button" data-cart-decrease aria-label="Decrease quantity">−</button>
            <span>${item.quantity}</span>
            <button type="button" data-cart-increase aria-label="Increase quantity">+</button>
          </div>
          <strong>${formatMoney(item.final_line_price)}</strong>
        </div>
      </div>
    </article>`;

  const renderCart = (cart) => {
    $$('[data-header-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
    $$('[data-cart-count]').forEach((node) => { node.textContent = `(${cart.item_count})`; });
    $('[data-cart-open]')?.setAttribute('aria-label', `Shopping bag with ${cart.item_count} items`);
    if (!cartContent) return;

    if (!cart.item_count) {
      cartContent.innerHTML = `
        <div class="emptyBag">
          <h3>Your next route starts here.</h3>
          <p>Explore insulated rideware built for daily carry.</p>
          <a class="primaryButton" href="${escapeHtml(routes.allProducts || '/collections/all')}">Shop rideware <span aria-hidden="true">→</span></a>
        </div>`;
      return;
    }

    const threshold = Number(config.shippingThreshold || 6500);
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100;
    cartContent.innerHTML = `
      <div class="cartItems">${cart.items.map(cartItemHtml).join('')}</div>
      <div class="cartSummary">
        <div><span>Subtotal</span><strong>${formatMoney(cart.total_price)}</strong></div>
        <p data-shipping-message>${remaining ? `${formatMoney(remaining)} away from free shipping.` : 'You earned free shipping.'}</p>
        <div class="shippingMeter"><span style="width:${progress}%"></span></div>
        <a class="checkoutButton" href="/checkout">Checkout</a>
        <small>Taxes and shipping calculated at checkout.</small>
      </div>`;
  };

  const getCart = async () => {
    const response = await fetch(routes.cartJson || '/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load bag');
    return response.json();
  };

  const refreshCart = async () => {
    const cart = await getCart();
    renderCart(cart);
    return cart;
  };

  const changeCartLine = async (key, quantity) => {
    const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Unable to update bag');
    const cart = await response.json();
    renderCart(cart);
    announce('Shopping bag updated.');
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ajax-add]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[type="submit"]');
    submit?.setAttribute('disabled', 'disabled');
    try {
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' }
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Unable to add this item');
      }
      await refreshCart();
      openCart();
      announce('Added to your shopping bag.');
    } catch (error) {
      announce(error.message);
    } finally {
      submit?.removeAttribute('disabled');
    }
  });

  document.addEventListener('click', async (event) => {
    const target = event.target.closest('button, a');
    if (!target) return;

    const cardSwatch = target.closest('[data-card-swatch]');
    if (cardSwatch) {
      event.preventDefault();
      const card = cardSwatch.closest('[data-product-card]');
      if (!card) return;

      $$('[data-card-swatch]', card).forEach((swatch) => {
        const isSelected = swatch === cardSwatch;
        swatch.classList.toggle('selected', isSelected);
        swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
      });

      const image = $('[data-card-image]', card);
      if (image && cardSwatch.dataset.cardImageSrc) {
        image.classList.add('isSwitching');
        const revealImage = () => image.classList.remove('isSwitching');
        image.addEventListener('load', revealImage, { once: true });
        image.srcset = cardSwatch.dataset.cardImageSrcset || '';
        image.src = cardSwatch.dataset.cardImageSrc;
        image.alt = cardSwatch.dataset.cardImageAlt || '';
        if (image.complete) window.requestAnimationFrame(revealImage);
      }

      $$('[data-card-link]', card).forEach((link) => {
        link.href = cardSwatch.dataset.cardUrl || link.href;
      });
      $('[data-card-price]', card).textContent = cardSwatch.dataset.cardPriceValue || '';
      const meta = $('[data-card-color-label]', card);
      if (meta) meta.textContent = cardSwatch.dataset.cardMeta || cardSwatch.dataset.cardLabel || '';

      const variantInput = $('[data-card-variant-input]', card);
      if (variantInput) variantInput.value = cardSwatch.dataset.cardVariantId || variantInput.value;
      card.dataset.activeVariant = cardSwatch.dataset.cardVariantId || '';

      const quickAdd = $('[data-card-quick-add]', card);
      const isAvailable = cardSwatch.dataset.cardAvailable === 'true';
      if (quickAdd) {
        quickAdd.disabled = !isAvailable;
        quickAdd.setAttribute('aria-label', `${isAvailable ? 'Quick add' : 'Sold out'} — ${cardSwatch.dataset.cardLabel || 'selected finish'}`);
        $('[data-card-quick-icon]', quickAdd)?.toggleAttribute('hidden', !isAvailable);
        $('[data-card-sold-out]', quickAdd)?.toggleAttribute('hidden', isAvailable);
      }

      announce(`Showing ${cardSwatch.dataset.cardLabel || 'selected finish'}.`);
      return;
    }

    if (target.matches('[data-menu-open]')) openMenu();
    if (target.matches('[data-menu-close]')) closeMenu();
    if (target.matches('[data-search-open]')) openSearch();
    if (target.matches('[data-search-close]')) closeSearch();
    if (target.matches('[data-cart-open]')) openCart();
    if (target.matches('[data-cart-close], [data-cart-backdrop]')) closeCart();

    const line = target.closest('[data-cart-line]');
    if (line && (target.matches('[data-cart-increase]') || target.matches('[data-cart-decrease]'))) {
      const quantity = Number($('.miniQuantity span', line)?.textContent || 0) + (target.matches('[data-cart-increase]') ? 1 : -1);
      target.setAttribute('disabled', 'disabled');
      try { await changeCartLine(line.dataset.key, Math.max(0, quantity)); }
      catch (error) { announce(error.message); }
    }

    if (target.matches('[data-filter-toggle]')) {
      $('[data-filter-panel]')?.classList.add('open');
      setBodyLock();
    }
    if (target.matches('[data-filter-close]')) {
      $('[data-filter-panel]')?.classList.remove('open');
      setBodyLock();
    }

    const favorite = target.closest('[data-favorite]');
    if (favorite) {
      const favorites = new Set(JSON.parse(localStorage.getItem('atlistFavorites') || '[]'));
      favorites.has(favorite.dataset.favorite) ? favorites.delete(favorite.dataset.favorite) : favorites.add(favorite.dataset.favorite);
      localStorage.setItem('atlistFavorites', JSON.stringify([...favorites]));
      favorite.classList.toggle('hearted', favorites.has(favorite.dataset.favorite));
      favorite.setAttribute('aria-pressed', favorites.has(favorite.dataset.favorite) ? 'true' : 'false');
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeMenu(); closeSearch(); closeCart();
    $('[data-filter-panel]')?.classList.remove('open');
    setBodyLock();
  });

  $$('[data-sort-select]').forEach((select) => select.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    window.location.assign(url.toString());
  }));

  const allProductGalleryThumbs = (root) => $$('[data-media-src]', root);
  const productGalleryThumbs = (root) => allProductGalleryThumbs(root).filter((thumb) => !thumb.hidden);

  const mediaPath = (source) => {
    if (!source) return '';
    try {
      return new URL(source, window.location.href).pathname.split('/').pop();
    } catch {
      return String(source).split('?')[0].split('/').pop();
    }
  };

  const updateProductGalleryControls = (root) => {
    const thumbs = productGalleryThumbs(root);
    thumbs.forEach((thumb, index) => {
      thumb.setAttribute('aria-label', `View image ${index + 1} of ${thumbs.length}`);
    });
    const hideArrows = thumbs.length <= 1;
    const previous = $('[data-gallery-previous]', root);
    const next = $('[data-gallery-next]', root);
    if (previous) previous.hidden = hideArrows;
    if (next) next.hidden = hideArrows;
    const status = $('[data-gallery-status]', root);
    const activeIndex = thumbs.findIndex((thumb) => thumb.classList.contains('active'));
    if (status && thumbs.length > 0) {
      status.textContent = `Image ${Math.max(0, activeIndex) + 1} of ${thumbs.length}`;
    }
  };

  const setProductGalleryMedia = (root, requestedIndex, shouldAnnounce = true) => {
    const thumbs = productGalleryThumbs(root);
    const image = $('.detailImage', root);
    if (!image || !thumbs.length) return;

    const index = (requestedIndex + thumbs.length) % thumbs.length;
    const thumb = thumbs[index];
    image.srcset = thumb.dataset.mediaSrcset || '';
    image.sizes = '(min-width: 760px) 58vw, 100vw';
    image.src = thumb.dataset.mediaSrc;
    image.alt = thumb.dataset.mediaAlt || '';
    root.dataset.activeMediaIndex = String(index);

    allProductGalleryThumbs(root).forEach((button) => {
      const isActive = button === thumb;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    if (shouldAnnounce) {
      const status = $('[data-gallery-status]', root);
      if (status) status.textContent = `Image ${index + 1} of ${thumbs.length}`;
    }
  };

  const filterProductGallery = (root, variant, shouldAnnounce = true) => {
    const allThumbs = allProductGalleryThumbs(root);
    if (!allThumbs.length || !variant) return;

    const variantKey = `|${variant.id}|`;
    const hasMediaGroups = allThumbs.some((thumb) => thumb.dataset.mediaVariantIds);
    const hasVariantGroup = allThumbs.some((thumb) => thumb.dataset.mediaVariantIds?.includes(variantKey));
    const shouldFilter = hasMediaGroups && hasVariantGroup;

    allThumbs.forEach((thumb) => {
      const groupIds = thumb.dataset.mediaVariantIds || '';
      thumb.hidden = shouldFilter && Boolean(groupIds) && !groupIds.includes(variantKey);
    });

    const thumbs = productGalleryThumbs(root);
    updateProductGalleryControls(root);
    if (!thumbs.length) return;

    const featuredPath = mediaPath(variant.featured_image?.src);
    const featuredThumb = featuredPath
      ? thumbs.find((thumb) => mediaPath(thumb.dataset.mediaSrc) === featuredPath)
      : null;
    const currentThumb = thumbs.find((thumb) => thumb.classList.contains('active'));
    const preferredThumb = featuredThumb || currentThumb || thumbs[0];
    setProductGalleryMedia(root, thumbs.indexOf(preferredThumb), shouldAnnounce);
  };

  const moveProductGallery = (root, direction) => {
    const thumbs = productGalleryThumbs(root);
    if (!thumbs.length) return;
    const activeIndex = Number(root.dataset.activeMediaIndex ?? thumbs.findIndex((thumb) => thumb.classList.contains('active')));
    setProductGalleryMedia(root, (activeIndex < 0 ? 0 : activeIndex) + direction);
  };

  const initProductGallery = (root) => {
    if (!root || root.dataset.galleryInitialized === 'true') return;
    root.dataset.galleryInitialized = 'true';
    const viewport = $('[data-gallery-viewport]', root);
    const allThumbs = allProductGalleryThumbs(root);
    if (!viewport || !allThumbs.length) return;

    allThumbs.forEach((thumb) => thumb.addEventListener('click', () => {
      const visibleThumbs = productGalleryThumbs(root);
      const index = visibleThumbs.indexOf(thumb);
      if (index >= 0) setProductGalleryMedia(root, index);
    }));
    $('[data-gallery-previous]', root)?.addEventListener('click', () => moveProductGallery(root, -1));
    $('[data-gallery-next]', root)?.addEventListener('click', () => moveProductGallery(root, 1));

    viewport.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      moveProductGallery(root, event.key === 'ArrowLeft' ? -1 : 1);
    });

    let touchStart;
    viewport.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }, { passive: true });
    viewport.addEventListener('touchend', (event) => {
      if (!touchStart) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      const distanceX = touch.clientX - touchStart.x;
      const distanceY = touch.clientY - touchStart.y;
      touchStart = null;
      if (Math.abs(distanceX) < 48 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
      moveProductGallery(root, distanceX > 0 ? -1 : 1);
    }, { passive: true });

    const variantsNode = $('[data-product-variants]', root);
    const selectedVariantId = Number($('[data-variant-id]', root)?.value);
    const variants = variantsNode ? JSON.parse(variantsNode.textContent) : [];
    const selectedVariant = variants.find((variant) => Number(variant.id) === selectedVariantId);
    if (selectedVariant) {
      filterProductGallery(root, selectedVariant, false);
    } else {
      const visibleThumbs = productGalleryThumbs(root);
      const initialIndex = Math.max(0, visibleThumbs.findIndex((thumb) => thumb.classList.contains('active')));
      root.dataset.activeMediaIndex = String(initialIndex);
      updateProductGalleryControls(root);
    }
  };

  const initProduct = (root) => {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const variantsNode = $('[data-product-variants]', root);
    const form = $('[data-product-form]', root);
    if (!variantsNode || !form) return;
    const variants = JSON.parse(variantsNode.textContent);
    const optionGroups = $$('[data-option-index]', form);
    const hiddenId = $('[data-variant-id]', form);
    const price = $('[data-product-price]', root);
    const addButton = $('[data-add-button]', form);
    const addLabel = $('[data-add-label]', form);
    const quantityInput = $('[data-product-quantity]', form);
    const quantityValue = $('[data-quantity-value]', form);

    const selectedOptions = () => optionGroups.map((group) => $('[data-option-value].selected', group)?.dataset.optionValue || '');
    const updateVariant = () => {
      const options = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));
      if (!variant) {
        addButton.disabled = true;
        addLabel.textContent = 'Unavailable';
        return;
      }
      hiddenId.value = variant.id;
      price.textContent = formatMoney(variant.price);
      addButton.disabled = !variant.available;
      addLabel.textContent = variant.available ? 'Add to bag' : 'Sold out';
      filterProductGallery(root, variant);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    optionGroups.forEach((group) => group.addEventListener('click', (event) => {
      const choice = event.target.closest('[data-option-value]');
      if (!choice) return;
      $$('[data-option-value]', group).forEach((button) => {
        button.classList.toggle('selected', button === choice);
        button.setAttribute('aria-pressed', button === choice ? 'true' : 'false');
      });
      $('[data-selected-option]', group).textContent = choice.dataset.optionValue;
      updateVariant();
    }));

    $('[data-quantity-minus]', form)?.addEventListener('click', () => {
      const next = Math.max(1, Number(quantityInput.value) - 1);
      quantityInput.value = next; quantityValue.textContent = next;
    });
    $('[data-quantity-plus]', form)?.addEventListener('click', () => {
      const next = Math.min(99, Number(quantityInput.value) + 1);
      quantityInput.value = next; quantityValue.textContent = next;
    });
  };

  $$('[data-product-root]').forEach((root) => {
    initProduct(root);
    initProductGallery(root);
  });

  try {
    const favorites = new Set(JSON.parse(localStorage.getItem('atlistFavorites') || '[]'));
    $$('[data-favorite]').forEach((button) => {
      const isSaved = favorites.has(button.dataset.favorite);
      button.classList.toggle('hearted', isSaved);
      button.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
    });
  } catch (_) { /* local storage can be disabled */ }

  document.addEventListener('shopify:section:load', (event) => {
    const root = $('[data-product-root]', event.target);
    initProduct(root);
    initProductGallery(root);
  });
})();
